import React, { useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Autocomplete,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Button,
  IconButton,
  MenuItem,
  Select
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useModalContext } from './ModalContext';
import { deepTrim } from 'helpers/deepTrim';
import { safeJsonParse, safeJsonStringify } from 'helpers/safeJson';
import { logError } from 'helpers/logger';

const clone = (x) => JSON.parse(JSON.stringify(x));

const objectPath = (o, fieldPath) => {
  let value = o;
  for (const field of fieldPath) {
    value = value?.[field];
  }
  return value;
};

const setObjectPath = (o, fieldPath, newValue) => {
  let value = o;
  for (const field of fieldPath.slice(0, fieldPath.length - 1)) {
    if (value[field] === undefined || value[field] === null) {
      value[field] = {};
    }
    value = value[field];
  }
  value[fieldPath[fieldPath.length - 1]] = newValue;
};

const useIntegrationGeneral = (fieldPath) => {
  const { formState, setFormState } = useModalContext();
  const value = objectPath(formState.values, fieldPath);
  const setField = (newValue) => {
    try {
      const updated = clone(formState);
      setObjectPath(updated.values, fieldPath, newValue);
      setFormState(updated);
    } catch (error) {
      logError('seedTalent: could not update field', error, { fieldPath });
    }
  };
  return [value, setField];
};

const useJsonField = (jsonFieldName, fieldPath) => {
  const [raw, setRaw] = useIntegrationGeneral([jsonFieldName]);
  const parsed = safeJsonParse(raw, `seedTalent(${jsonFieldName})`);
  const value = objectPath(parsed, fieldPath);
  const setField = (newValue) => {
    try {
      const updated = clone(parsed);
      setObjectPath(updated, fieldPath, newValue);
      setRaw(safeJsonStringify(updated, `seedTalent(${jsonFieldName})`));
    } catch (error) {
      logError('seedTalent: could not update field', error, {
        jsonFieldName,
        fieldPath
      });
    }
  };
  return [value, setField];
};

export const useIntegrationConfig = (fieldPath) =>
  useJsonField('config', fieldPath);
const useIntegrationSource = (fieldPath) => useJsonField('source', fieldPath);

export const SectionCard = ({ title, subheader, children }) => (
  <Card variant="outlined">
    <CardHeader
      subheader={subheader}
      subheaderTypographyProps={{
        sx: { color: 'primary.main', fontWeight: 500 }
      }}
      title={title}
      titleTypographyProps={{ sx: { fontWeight: 700 } }}
    />
    <Divider />
    <CardContent>{children}</CardContent>
  </Card>
);

export const SeedTalentSecretField = ({ label, secret, setState, error }) => {
  const mode = secret?.type === 'encrypted' ? 'encrypted' : 'decrypted';

  const switchMode = (newMode) => {
    if (newMode === 'encrypted') {
      setState({ type: 'encrypted', iv: '', value: '' });
    } else {
      setState('');
    }
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Select
          onChange={(e) => switchMode(e.target.value)}
          size="small"
          value={mode}
        >
          <MenuItem value="decrypted">Plain text</MenuItem>
          <MenuItem value="encrypted">Already encrypted</MenuItem>
        </Select>
      </Grid>
      {mode === 'encrypted' ? (
        <>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              label={`${label} value`}
              onChange={(e) => setState({ ...secret, value: e.target.value })}
              value={secret?.value || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              label={`${label} iv`}
              onChange={(e) => setState({ ...secret, iv: e.target.value })}
              value={secret?.iv || ''}
              variant="standard"
            />
          </Grid>
        </>
      ) : (
        <Grid item xs={12}>
          <TextField
            error={!!error}
            fullWidth
            helperText={error || 'Stored encrypted when the integration is saved'}
            label={label}
            onChange={(e) => setState(e.target.value)}
            required
            value={typeof secret === 'string' ? secret : ''}
            variant="standard"
          />
        </Grid>
      )}
    </Grid>
  );
};

export const DEFAULT_SLACK_SERVICE = 'B0BDYV27ESH/FUYupw2qPgFFcC1yYV5sTEDL';

export const SEED_TALENT_BASE_URL = 'https://api.seedtalent.com';

export const RUNTIME_OWNED_CONFIG_KEYS = [
  'status',
  'attempts',
  'notification',
  'recoveryLockUntil',
  'lastPollDate'
];

export const useSeedTalentDefaults = () => {
  const { formState, setFormState } = useModalContext();
  useEffect(() => {
    try {
      const config = safeJsonParse(
        formState.values.config,
        'useSeedTalentDefaults(config)'
      );
      const companyShortName =
        formState.values.companyName ||
        config.shortName ||
        config.authentication?.companyName ||
        '';
      const configWithDefaults = {
        testOnly: false,
        customSlackService: DEFAULT_SLACK_SERVICE,
        ...config,
        shortName: companyShortName,
        seedTalent: {
          baseUrl: SEED_TALENT_BASE_URL,
          platformApiKey: '',
          orgIntegrationKey: '',
          ...config.seedTalent
        },
        emailConfig: {
          emailContacts: [],
          sendSuccessEmails: 'true',
          ...config.emailConfig
        },
        authentication: {
          ...config.authentication,
          companyName: companyShortName
        }
      };

      const source = safeJsonParse(
        formState.values.source,
        'useSeedTalentDefaults(source)'
      );
      const sourceWithDefaults = {
        hashVerification: true,
        ...source,
        type: 'webhook'
      };

      setFormState({
        ...formState,
        values: {
          ...formState.values,
          companyName: companyShortName,
          runnable: formState.values.runnable ?? false,
          config: safeJsonStringify(
            configWithDefaults,
            'useSeedTalentDefaults(config)'
          ),
          source: safeJsonStringify(
            sourceWithDefaults,
            'useSeedTalentDefaults(source)'
          )
        }
      });
    } catch (error) {
      logError('useSeedTalentDefaults: could not seed defaults', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const errorProps = (errors, key, helperText) =>
  errors?.[key] ? { error: true, helperText: errors[key] } : { helperText };

const useCompanyShortName = () => {
  const { formState, setFormState } = useModalContext();
  const value = formState.values.companyName;
  const setValue = (newValue) => {
    try {
      const config = safeJsonParse(
        formState.values.config,
        'useCompanyShortName'
      );
      config.shortName = newValue;
      config.authentication = {
        ...config.authentication,
        companyName: newValue
      };
      setFormState({
        ...formState,
        values: {
          ...formState.values,
          companyName: newValue,
          config: safeJsonStringify(config, 'useCompanyShortName')
        }
      });
    } catch (error) {
      logError('useCompanyShortName: could not update company name', error);
    }
  };
  return [value, setValue];
};

export const SeedTalentDetailsCard = ({ errors }) => {
  const { formState, isUpdate } = useModalContext();
  const [companyShortName, setCompanyShortName] = useCompanyShortName();
  const [runnable, setRunnable] = useIntegrationGeneral(['runnable']);

  return (
    <SectionCard
      subheader="Identifies this integration and the client it belongs to"
      title="Integration details"
    >
      <Grid container spacing={2}>
        {isUpdate && (
          <Grid item xs={12}>
            <TextField
              disabled
              fullWidth
              label="Integration ID"
              value={formState.values.integrationId || ''}
              variant="standard"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Company short name"
            onChange={(e) => setCompanyShortName(e.target.value)}
            required
            value={companyShortName || ''}
            variant="standard"
            {...errorProps(
              errors,
              'companyName',
              'Company short name in Wurk - also used to identify the client in notification emails'
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!runnable}
                onChange={(e, checked) => setRunnable(checked)}
              />
            }
            label={
              <>
                Runnable
                <FormHelperText>
                  Allows this integration to be triggered manually from the
                  integrations list
                </FormHelperText>
              </>
            }
          />
        </Grid>
      </Grid>
    </SectionCard>
  );
};

export const SeedTalentApiCard = ({ errors }) => {
  const [baseUrl, setBaseUrl] = useIntegrationConfig([
    'seedTalent',
    'baseUrl'
  ]);
  const [platformApiKey, setPlatformApiKey] = useIntegrationConfig([
    'seedTalent',
    'platformApiKey'
  ]);
  const [orgIntegrationKey, setOrgIntegrationKey] = useIntegrationConfig([
    'seedTalent',
    'orgIntegrationKey'
  ]);

  return (
    <SectionCard
      subheader="Credentials the client sets up in Seed Talent"
      title="Seed Talent API"
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Base URL"
            onChange={(e) => setBaseUrl(e.target.value)}
            required
            value={baseUrl || ''}
            variant="standard"
            {...errorProps(errors, 'seedTalent.baseUrl')}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <SeedTalentSecretField
            error={errors?.['seedTalent.platformApiKey']}
            label="Platform API key"
            secret={platformApiKey ?? ''}
            setState={setPlatformApiKey}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <SeedTalentSecretField
            error={errors?.['seedTalent.orgIntegrationKey']}
            label="Org integration key"
            secret={orgIntegrationKey ?? ''}
            setState={setOrgIntegrationKey}
          />
        </Grid>
      </Grid>
    </SectionCard>
  );
};

export const WebhookSourceCard = ({ errors }) => {
  const [secretKey, setSecretKey] = useIntegrationSource(['secretKey']);
  const [hashVerification, setHashVerification] = useIntegrationSource([
    'hashVerification'
  ]);

  return (
    <SectionCard
      subheader="Must match the secret configured on the Wurk webhook"
      title="Webhook"
    >
      <Grid container spacing={2}>
        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            label="Secret key"
            onChange={(e) => setSecretKey(e.target.value)}
            required
            value={secretKey || ''}
            variant="standard"
            {...errorProps(errors, 'source.secretKey')}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hashVerification !== false}
                onChange={(e, checked) => setHashVerification(checked)}
              />
            }
            label={
              <>
                Verify payload hash
                <FormHelperText>
                  Rejects deliveries whose signature does not match the secret
                </FormHelperText>
              </>
            }
          />
        </Grid>
      </Grid>
    </SectionCard>
  );
};

export const CostCenterCard = ({ errors }) => {
  const [useCostCenter, setUseCostCenter] = useIntegrationConfig([
    'useCostCenter'
  ]);
  const [costCenterNumber, setCostCenterNumber] = useIntegrationConfig([
    'costCenterNumber'
  ]);
  const [costCenterBaseLevel, setCostCenterBaseLevel] = useIntegrationConfig([
    'costCenterBaseLevel'
  ]);
  const [costCenterFixedLevel, setCostCenterFixedLevel] = useIntegrationConfig([
    'costCenterFixedLevel'
  ]);

  const numberField = (label, value, setValue, errorKey, helperText) => (
    <Grid item md={4} xs={12}>
      <TextField
        fullWidth
        label={label}
        onChange={(e) =>
          setValue(e.target.value === '' ? '' : Number(e.target.value))
        }
        required
        type="number"
        value={value ?? ''}
        variant="standard"
        {...errorProps(errors, errorKey, helperText)}
      />
    </Grid>
  );

  return (
    <SectionCard
      subheader="Derives the employee's group from their cost center instead of their EIN name"
      title="Cost center"
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={!!useCostCenter}
            onChange={(e, checked) => setUseCostCenter(checked)}
          />
        }
        label="Use cost center for group mapping"
      />
      {useCostCenter && (
        <Grid container spacing={2} sx={{ marginTop: 0 }}>
          {numberField(
            'Cost center number',
            costCenterNumber,
            setCostCenterNumber,
            'costCenterNumber',
            'The DefaultCostCenter<N> slot to read'
          )}
          {numberField(
            'Base level',
            costCenterBaseLevel,
            setCostCenterBaseLevel,
            'costCenterBaseLevel',
            'First half of the group mapping key'
          )}
          {numberField(
            'Fixed level',
            costCenterFixedLevel,
            setCostCenterFixedLevel,
            'costCenterFixedLevel',
            'Second half of the group mapping key'
          )}
        </Grid>
      )}
    </SectionCard>
  );
};

export const GroupMappingCard = ({ errors }) => {
  const [defaultGroup, setDefaultGroup] = useIntegrationConfig([
    'defaultGroup'
  ]);

  return (
    <>
      <SectionCard
        subheader="Used when an employee matches no mapping below"
        title="Default group"
      >
        <TextField
          fullWidth
          label="Default group ID"
          onChange={(e) => setDefaultGroup(e.target.value)}
          required
          value={defaultGroup || ''}
          variant="standard"
          {...errorProps(errors, 'defaultGroup', 'Seed Talent group UUID')}
        />
      </SectionCard>
      <GroupMappingCardTable />
    </>
  );
};

const GroupMappingCardTable = () => {
  const [mapping, setMapping] = useIntegrationConfig(['groupMapping']);
  const rows = Object.entries(mapping || {});

  const addRow = () => setMapping({ ...(mapping || {}), '': '' });
  const deleteRow = (key) =>
    setMapping(
      Object.fromEntries(rows.filter(([existing]) => existing !== key))
    );
  const changeKey = (key, newKey) =>
    setMapping(
      Object.fromEntries(
        rows.map(([k, v]) => (k === key ? [newKey, v] : [k, v]))
      )
    );
  const changeValue = (key, newValue) =>
    setMapping(
      Object.fromEntries(
        rows.map(([k, v]) => (k === key ? [k, newValue] : [k, v]))
      )
    );

  return (
    <SectionCard
      subheader="Maps each EIN name or cost center name to the Seed Talent group it belongs to"
      title="Group mapping"
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>EIN name or cost center name</TableCell>
            <TableCell>Seed Talent group ID</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length > 0 ? (
            rows.map(([key, value]) => (
              <GroupMappingRow
                changeKey={changeKey}
                changeValue={changeValue}
                deleteRow={deleteRow}
                key={key}
                mappingKey={key}
                value={value}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3}>No mapping configured</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Button onClick={addRow} startIcon={<AddIcon />} variant="outlined">
        Add row
      </Button>
    </SectionCard>
  );
};

const GroupMappingRow = ({
  mappingKey,
  value,
  changeKey,
  changeValue,
  deleteRow
}) => {
  const [draftKey, setDraftKey] = React.useState(mappingKey);
  return (
    <TableRow>
      <TableCell>
        <TextField
          fullWidth
          label="EIN name or cost center name"
          onBlur={() => changeKey(mappingKey, draftKey)}
          onChange={(e) => setDraftKey(e.target.value)}
          placeholder="EIN name, or baseLevel/fixedLevel when using cost centers"
          value={draftKey}
          variant="standard"
        />
      </TableCell>
      <TableCell>
        <TextField
          fullWidth
          label="Seed Talent group ID"
          onChange={(e) => changeValue(mappingKey, e.target.value)}
          placeholder="Group UUID"
          value={value}
          variant="standard"
        />
      </TableCell>
      <TableCell>
        <IconButton aria-label="Delete row" onClick={() => deleteRow(mappingKey)}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const BLOCKLIST_FIELDS = [
  { key: 'costCenterName', label: 'Cost center name' },
  { key: 'costCenterBaseLevel', label: 'Base level' },
  { key: 'costCenterFixedLevel', label: 'Fixed level' }
];

export const CostCenterBlocklistCard = () => {
  const [blocklist, setBlocklist] = useIntegrationConfig([
    'costCenterBlocklist'
  ]);
  const rows = blocklist || [];

  const addRow = () => setBlocklist([...rows, {}]);
  const deleteRow = (index) =>
    setBlocklist(rows.filter((_, i) => i !== index));
  const changeField = (index, key, value) =>
    setBlocklist(
      rows.map((row, i) => {
        if (i !== index) {
          return row;
        }
        const updated = { ...row };
        if (value === '') {
          delete updated[key];
        } else {
          updated[key] = value;
        }
        return updated;
      })
    );

  return (
    <SectionCard
      subheader="Employees matching any row are skipped - a blank column matches anything"
      title="Cost center blocklist"
    >
      <Table>
        <TableHead>
          <TableRow>
            {BLOCKLIST_FIELDS.map(({ key, label }) => (
              <TableCell key={key}>{label}</TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length > 0 ? (
            rows.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={index}>
                {BLOCKLIST_FIELDS.map(({ key, label }) => (
                  <TableCell key={key}>
                    <TextField
                      fullWidth
                      label={label}
                      onChange={(e) =>
                        changeField(index, key, e.target.value)
                      }
                      value={row[key] || ''}
                      variant="standard"
                    />
                  </TableCell>
                ))}
                <TableCell>
                  <IconButton
                    aria-label="Delete row"
                    onClick={() => deleteRow(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={BLOCKLIST_FIELDS.length + 1}>
                No cost centers blocked
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Button onClick={addRow} startIcon={<AddIcon />} variant="outlined">
        Add row
      </Button>
    </SectionCard>
  );
};

export const SeedTalentNotificationsCard = () => {
  const [emailContacts, setEmailContacts] = useIntegrationConfig([
    'emailConfig',
    'emailContacts'
  ]);
  const [sendSuccessEmails, setSendSuccessEmails] = useIntegrationConfig([
    'emailConfig',
    'sendSuccessEmails'
  ]);
  const [customSlackService, setCustomSlackService] = useIntegrationConfig([
    'customSlackService'
  ]);
  const [testOnly, setTestOnly] = useIntegrationConfig(['testOnly']);

  return (
    <SectionCard
      subheader="Who gets notified when an employee is synced"
      title="Notifications"
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            multiple
            onChange={(e, newValue) => setEmailContacts(newValue)}
            options={[]}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                helperText="Press Enter after each address - leave blank for no notifications"
                label="Email recipients"
                placeholder="Add an email address"
                variant="standard"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  size="small"
                  variant="outlined"
                  {...getTagProps({ index })}
                />
              ))
            }
            value={emailContacts || []}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            helperText="Slack webhook path for error notifications"
            label="Custom Slack service"
            onChange={(e) => setCustomSlackService(e.target.value)}
            value={customSlackService || ''}
            variant="standard"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendSuccessEmails === 'true'}
                onChange={(e, checked) =>
                  setSendSuccessEmails(checked ? 'true' : 'false')
                }
              />
            }
            label={
              <>
                Send on successful runs
                <FormHelperText>In addition to failures</FormHelperText>
              </>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!testOnly}
                onChange={(e, checked) => setTestOnly(checked)}
              />
            }
            label={
              <>
                Test mode
                <FormHelperText>
                  Only syncs employees whose name or email contains "test"
                </FormHelperText>
              </>
            }
          />
        </Grid>
      </Grid>
    </SectionCard>
  );
};

const isBlankSecret = (value) => {
  if (value === undefined || value === null || value === '') {
    return true;
  }
  if (typeof value === 'object') {
    return !value.value || !value.iv;
  }
  return false;
};

export const trimSeedTalentValues = (values) => {
  try {
    const trimmed = { ...values };
    if (typeof values.companyName === 'string') {
      trimmed.companyName = values.companyName.trim();
    }
    if (values.config) {
      trimmed.config = safeJsonStringify(
        deepTrim(safeJsonParse(values.config, 'trimSeedTalentValues(config)')),
        'trimSeedTalentValues(config)'
      );
    }
    if (values.source) {
      trimmed.source = safeJsonStringify(
        deepTrim(safeJsonParse(values.source, 'trimSeedTalentValues(source)')),
        'trimSeedTalentValues(source)'
      );
    }
    return trimmed;
  } catch (error) {
    logError('trimSeedTalentValues: could not trim values', error);
    return values;
  }
};

export const validateSeedTalentIntegration = (rawValues) => {
  try {
    return runSeedTalentValidation(rawValues);
  } catch (error) {
    logError('validateSeedTalentIntegration: validation failed', error);
    return {
      companyName:
        'The configuration could not be validated. Check the browser console for details.'
    };
  }
};

const runSeedTalentValidation = (rawValues) => {
  const errors = {};
  let config = {};
  let source = {};
  try {
    config = deepTrim(JSON.parse(rawValues.config || '{}'));
  } catch (error) {
    logError('validateSeedTalentIntegration: config is not valid JSON', error);
    return { config: 'Configuration is not valid JSON' };
  }
  try {
    source = deepTrim(JSON.parse(rawValues.source || '{}'));
  } catch (error) {
    logError('validateSeedTalentIntegration: source is not valid JSON', error);
    return { source: 'Source is not valid JSON' };
  }
  const values = {
    ...rawValues,
    companyName:
      typeof rawValues.companyName === 'string'
        ? rawValues.companyName.trim()
        : rawValues.companyName
  };

  if (
    !values.companyName ||
    !config.shortName ||
    !config.authentication?.companyName
  ) {
    errors.companyName = 'Company short name is required';
  }
  if (!config.seedTalent?.baseUrl) {
    errors['seedTalent.baseUrl'] = 'Base URL is required';
  }
  if (isBlankSecret(config.seedTalent?.platformApiKey)) {
    errors['seedTalent.platformApiKey'] = 'Platform API key is required';
  }
  if (isBlankSecret(config.seedTalent?.orgIntegrationKey)) {
    errors['seedTalent.orgIntegrationKey'] = 'Org integration key is required';
  }
  if (!source.secretKey) {
    errors['source.secretKey'] = 'Webhook secret key is required';
  }
  if (!config.defaultGroup) {
    errors.defaultGroup = 'Default group ID is required';
  }

  if (config.useCostCenter) {
    const levels = [
      ['costCenterNumber', 'Cost center number'],
      ['costCenterBaseLevel', 'Base level'],
      ['costCenterFixedLevel', 'Fixed level']
    ];
    for (const [key, label] of levels) {
      if (typeof config[key] !== 'number' || Number.isNaN(config[key])) {
        errors[key] = `${label} is required when using cost centers`;
      }
    }
  }

  const contacts = config.emailConfig?.emailContacts || [];
  const EMAIL_REGEX =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  if (contacts.some((contact) => !EMAIL_REGEX.test(contact))) {
    errors['emailConfig.emailContacts'] =
      'One or more email recipients are not valid addresses';
  }

  return errors;
};
